import React, { type ReactNode } from 'react';
import { StepProgressBarContext } from './context';
import { Step } from './step/step';
import './step-progress-bar.scss';
import cn from 'classnames';
import { StepProgressBarSkeleton } from './skeleton/step-progress-bar-skeleton';

interface StepProgressBarProps extends React.ComponentPropsWithoutRef<'ol'> {
    activeStep?: number;
    isLoading?: boolean;
    isComplete?: boolean;
    children: ReactNode;
}

export const StepProgressBar = ({
    activeStep,
    isLoading = false,
    isComplete,
    children,
    ...props
}: StepProgressBarProps) => {
    const numberOfSteps = React.Children.count(children);

    if (isLoading) {
        return <StepProgressBarSkeleton stepCount={numberOfSteps} />;
    }

    return (
        <ol
            aria-label="Steg"
            className={cn('step-progress-bar', { 'step-progress-bar--complete': isComplete })}
            {...props}
        >
            {React.Children.map(children, (step, index) => {
                const contextValue = {
                    activeStep,
                    isComplete,
                    index,
                };

                const isCompleteStep = activeStep && activeStep > index + 1;
                const isActiveStep = activeStep === index + 1;

                const classNames = cn('step-progress-bar__list-element__line', {
                    'step-progress-bar__list-element__line--active': isActiveStep,
                    'step-progress-bar__list-element__line--complete': isCompleteStep,
                });

                const getDataTestId = () => {
                    if (isCompleteStep) {
                        return 'complete-step';
                    }
                    if (isActiveStep) {
                        return 'active-step';
                    }
                };

                return (
                    <li
                        key={children?.toString?.() ?? ''}
                        data-testid={getDataTestId()}
                        className="step-progress-bar__list-element"
                        aria-current={isActiveStep ? 'step' : undefined}
                    >
                        <span className={cn('step-progress-bar__list-element__line--before', classNames)} />
                        <StepProgressBarContext.Provider value={contextValue}>{step}</StepProgressBarContext.Provider>
                        <span className={cn('step-progress-bar__list-element__line--after', classNames)} />
                    </li>
                );
            })}
        </ol>
    );
};

StepProgressBar.Step = Step;
